import axios from "axios";
import Asset from "@/api/asset";
import User from "@/api/user";
import Model from "@/api/model";
import Role from "@/api/role";
import Job from "@/api/job";
import Stat from "@/api/stat";
import Setting from "@/api/setting";
import Instance from "@/api/instance";
import Label from "@/api/label";
import Box from "@/api/box";
import Project from "@/api/project";
import TrainConfig from "@/api/train-config";
import Worker from "@/api/worker";
import AnnotationGroup from "@/api/annotation-group";
import ConfMatrixItem from "@/api/conf-matrix-item";
import Annotation from "@/api/annotation";
import Task from "@/api/task";
import UserTask from "@/api/user-task";
import UserSetting from "@/api/user-setting";
import dayjs from 'dayjs'
import AvailableModelParameter from "@/api/available-model-parameter";
import AvailableModel from "@/api/available-model";
import LabelSet from "@/api/label-set";
import Overview from "@/api/overview";
import Onboarding from "@/api/onboarding";
import Payment from "@/api/payment";
import SegAny from "@/api/segany";
import Sam3 from "@/api/sam3";
import Messages from "@/api/messages";
import SuperAdmin from "@/api/superadmin";
import Suggestions from "@/api/suggestions";
import CropDownload from "@/api/cropDownload";
import Report from "@/api/report";
import { TOKEN_EXPIRATION_TRUSTED_DEVICE, TOKEN_EXPIRATION_REGULAR_SESSION, TOKEN_REFRESH_THRESHOLD } from '@/config/constants';
import { clearAuthStorage, getStoredToken, restoreTokenFromStorage } from '@/util/auth';

const UNAUTHENTICATED_PAGE_NAMES = [
    'Login',
    'ResetPassword',
    'InitiateResetPassword',
    'FrontPage',
    'ConfirmEmail',
    'TryModel',
];

export default class Box21Api {
    constructor(vm, api_host, { store, router } = {}) {
        this.api_url = api_host;
        this.annotation = new Annotation(this);
        this.asset = new Asset(this)
        this.model = new Model(this);
        this.role = new Role(this);
        this.worker = new Worker(this);
        this.user = new User(this);
        this.instance = new Instance(this);
        this.setting = new Setting(this);
        this.job = new Job(this);
        this.box = new Box(this);
        this.project = new Project(this);
        this.label = new Label(this);
        this.stat = new Stat(this);
        this.train_config = new TrainConfig(this);
        this.conf_matrix_item = new ConfMatrixItem(this);
        this.annotation_group = new AnnotationGroup(this);
        this.task = new Task(this);
        this.user_task = new UserTask(this);
        this.user_setting = new UserSetting(this);
        this.available_model = new AvailableModel(this);
        this.available_model_parameter = new AvailableModelParameter(this);
        this.label_set = new LabelSet(this);
        this.overview = new Overview(this);
        this.onboarding = new Onboarding(this);
        this.payment = new Payment(this);
        this.segany = new SegAny(this);
        this.sam3 = new Sam3(this);
        this.messages = new Messages(this);
        this.superadmin = new SuperAdmin(this);
        this.suggestions = new Suggestions(this);
        this.crop_download = new CropDownload(this);
        this.report = new Report(this);
        this.vm = vm;
        this.store = store;
        this.router = router;
        this.on_unauthenticated_pages = () => UNAUTHENTICATED_PAGE_NAMES.includes(
            router.currentRoute.value.name,
        );
        this.sessionReadyPromise = null;
        restoreTokenFromStorage();
    }

    async ensureSessionReady() {
        restoreTokenFromStorage();
        if (!getStoredToken()) {
            return false;
        }
        if (this.store.getters['user/get_current_user']) {
            return true;
        }
        if (!this.sessionReadyPromise) {
            this.sessionReadyPromise = this.get_current_user({ suppressAuthRedirect: true })
                .finally(() => {
                    this.sessionReadyPromise = null;
                });
        }
        await this.sessionReadyPromise;
        return Boolean(this.store.getters['user/get_current_user']);
    }

    clear_session() {
        clearAuthStorage();
        this.token = null;
    }

    async register(first_name, last_name, email, password) {
        let formData = new FormData();
        formData.append('first_name', first_name);
        formData.append('last_name', last_name);
        formData.append('email', email);
        formData.append('password', password);
        return this.post_without_token('/user/register', formData)
    }

    async resend_confirmation_email(user_email) {
        let formData = new FormData();
        formData.append('email', user_email);
        // Endpoint does not require auth; use post_without_token so it works on the login page.
        return this.post_without_token('/user/resend-confirmation-mail', formData)
    }

    async confirm_email(confirmation_token) {
        let formData = new FormData();
        formData.append('confirmation_token', confirmation_token);
        return this.post_without_token('/user/confirm', formData)
    }

    async set_token(email, password, trustComputer = false, otp = null) {
        let response = await this.get_token(email, password, trustComputer, otp);
        if(!response) {
            return { success: false };
        }

        if (response.locked === true) {
            return {
                success: false,
                locked: true,
                message: response.message,
            };
        }

        if (typeof response === 'string') {
            return { success: false, message: response };
        }

        // No token was issued: either the credentials were wrong or a (valid)
        // 2FA code is required. The backend signals the latter with requires_2fa.
        if(!response.token) {
            return {
                success: false,
                requires_2fa: !!response.requires_2fa,
                message: response.message,
            };
        }

        this.token = response.token;
        localStorage.token = this.token;
        localStorage.token_expired_on = dayjs().add(response.expires_in_minutes, 'minutes').toISOString();

        this.store.commit('user/set_current_user', response.user);
        this.store.commit('user/set_current_project_id', sessionStorage.getItem('project_id'));

        // Store trust preference
        if (response.trusted_device) {
            localStorage.trusted_device = 'true';
            this.set_cookie('token', this.token, TOKEN_EXPIRATION_TRUSTED_DEVICE) // 30 days for trusted devices
        } else {
            localStorage.trusted_device = 'false';
            this.set_cookie('token', this.token, TOKEN_EXPIRATION_REGULAR_SESSION) // 1 hour for regular sessions
        }

        return { success: true }
    }

    async get_token(email, password, trustComputer = false, otp = null) {
        let formData = new FormData();
        formData.append('email', email);
        formData.append('password', password);
        formData.append('trust_computer', trustComputer.toString());
        if (otp) {
            formData.append('otp', otp);
        }

        let url = this.api_url + '/login';
        try {
            let response = await axios.post(url, formData, { headers: {} });
            return response.data;
        } catch (error) {
            // 401 JSON (e.g. requires_2fa) or 403 JSON (account locked) or legacy string bodies
            if (error && error.response && error.response.data !== undefined) {
                const d = error.response.data;
                if (typeof d === 'object' && d !== null && !Array.isArray(d)) {
                    return d;
                }
                if (typeof d === 'string') {
                    return { message: d, requires_2fa: false };
                }
            }
            console.log(error);
            return null;
        }
    }

    async get_current_user(options = {}) {
        const url = '/users/current-user';
        let formData = new FormData();
        let current_user = await this.post_with_token(url, formData, false, options)
        if(current_user) {
            this.store.commit('user/set_current_user', current_user);
            this.store.commit('user/set_current_project_id', sessionStorage.getItem('project_id'));
        }
        return current_user;
    }

    async refresh_token() {
        let url = '/refresh';
        let formData = new FormData();
        let response = await this.post_with_token(url, formData , true)

        if(response) {
            localStorage.token = response.token;
            localStorage.token_expired_on = dayjs().add(response.expires_in_minutes, 'minutes').toISOString();
            const isTrustedDevice = localStorage.getItem('trusted_device') === 'true';
            const expirationMinutes = isTrustedDevice
                ? TOKEN_EXPIRATION_TRUSTED_DEVICE
                : TOKEN_EXPIRATION_REGULAR_SESSION;
            this.set_cookie('token', response.token, expirationMinutes);
        }
    }


    async post_without_token(url, formData) {
        url = this.api_url + url;
        let this2 = this;
        let response = await axios.post(url, formData, {
            headers: {}
        }).catch((error) => {
            console.log(error)
            this2.store.dispatch('snackbars/add_failed_snack', 'An error occurred 1');
        })

        if(!response) {
            return null;
        }

        return response.data
    }

    async post_with_token(url, formData, skip_refresh=false, options = {}) {
        restoreTokenFromStorage();
        const token = getStoredToken();
        if (!token) {
            return null;
        }

        let diff = dayjs(localStorage.token_expired_on).diff(dayjs(), 'minutes');
        if(!skip_refresh && token && diff < TOKEN_REFRESH_THRESHOLD) {
            await this.refresh_token();
        }
        let config = {
            headers: {
                'x-access-token': getStoredToken()
            }
        }

        url = this.api_url + url;
        let this2 = this;
        let response = await axios.post(url, formData, config).catch((error)=>{
            if (error && error.response && error.response.status === 401) {
                clearAuthStorage();
                if (!options.suppressAuthRedirect && !this2.on_unauthenticated_pages()) {
                    const redirect = this2.router.currentRoute.value.fullPath;
                    this2.router.push({
                        name: 'Login',
                        query: redirect && redirect !== '/login' ? { redirect } : {},
                    });
                }
                return null;
            }
            else {
                console.log(error);
                // Return error response data so calling code can access error messages
                if (error && error.response && error.response.data) {
                    return { data: error.response.data };
                }
                this2.store.dispatch('snackbars/add_failed_snack', 'An error occurred 2');
                return null;
            }
        })

        if(!response) {
            return null;
        }

        return response.data
    }

    set_cookie(name, value, minutes) {
        let expires = "";
        if (minutes) {
            const date = new Date();
            date.setTime(date.getTime() + (minutes * 60 * 1000));
            expires = "; Expires=" + date.toUTCString();
        }
        document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/`;
    }

    async get(url, with_credentials) {


        if(!with_credentials) {
            with_credentials = false
        }

        let config = {
            headers: {
                'x-access-token': localStorage.token
            },
            with_credentials: with_credentials
        }
        url = this.api_url + url;
        //let this2 = this;


        let response = await axios.get(url, config).catch(()=>{
            //this2.vm.$router.push({name:'Login'});
        })
        return response.data;
    }

    async set_setting(name, value) {
        let formData = new FormData();
        formData.append('project_id', '1');
        formData.append('limit', '1');
        formData.append('name', name);
        formData.append('value', value);
        return this.post_with_token('/set_setting', formData)
    }

    async get_settings() {
        let formData = new FormData();
        formData.append('project_id', '1');
        return this.post_with_token('/settings', formData)
    }
}